/** The tech stacks Create Project offers for a Web project (GET /projects/stacks):
 *  the presets, the default first, and the Custom option's picker, or null while
 *  it isn't offered. The server decides what is offered; a project's stack is
 *  chosen once, when it is created. */
import api from '../utils/axios';

/** A preset: predefined picks of parts. */
export interface StackPreset {
  /** What the create request sends as `stack`. */
  id: string;
  /** Such as "React (Single app) + Node.js (Unified) + PostgreSQL". */
  label: string;
  /** What the stack includes, for its info icon. */
  info: string[];
  /** Its parts: group -> part id. */
  picks: Record<string, string>;
  /** Selected when the drawer opens; a Web clone of a Low Code project is built on it. */
  default: boolean;
}

/** An option of a group of the Custom option's picker. */
export interface StackPickerOption {
  /** What the create request sends for its group in `stack_picks`. */
  id: string;
  label: string;
  /** What it includes, for its info icon. */
  info: string;
  /** The groups that must be picked with it. */
  needs: string[];
  /** Whether the server can run it; reason says why not. */
  available: boolean;
  reason: string | null;
}

/** A group of the Custom option's picker: zero or one option is picked. */
export interface StackPickerGroup {
  id: string;
  label: string;
  /** An architecture's group, shown once its parent is picked. */
  parent: string | null;
  /** The groups that can't be picked with it. */
  conflicts_with: string[];
  /** What no pick means, for an architecture. */
  default_label: string | null;
  options: StackPickerOption[];
}

/** The Custom option of the Create Project drawer. */
export interface CustomStackPicker {
  instructions_max_chars: number;
  groups: StackPickerGroup[];
  /** For each server option, the databases it can be picked with. */
  pairs: Record<string, string[]>;
}

export interface StackCatalogue {
  presets: StackPreset[];
  custom: CustomStackPicker | null;
}

export async function fetchStackCatalogue(): Promise<StackCatalogue> {
  const response = await api.get('/projects/stacks');
  return { presets: response.data.presets ?? [], custom: response.data.custom ?? null };
}
